import { useLayoutEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import IconSvg from 'Components/IconSvg';

const formatEventDate = ({ endDate, startDate }) => {
  const getDate = (date) => {
    return dayjs(date).format('D MMMM YYYY').split(/\s/);
  };
  const [sDay, sMonth, sYear] = getDate(startDate);
  const [eDay, eMonth, eYear] = getDate(endDate);
  // Single-day event
  if (sDay === eDay && sMonth === eMonth && sYear === eYear)
    return `${sMonth} ${sDay}, ${sYear}`;
  // Single-month event
  if (sMonth === eMonth && sYear === eYear)
    return `${sMonth} ${sDay} - ${eDay}, ${eYear}`;
  // Single-year event
  if (sYear === eYear) return `${sMonth} ${sDay} - ${eMonth} ${eDay}, ${eYear}`;
  // Multi-year event
  return `${sMonth} ${sDay}, ${sYear} - ${eMonth} ${eDay}, ${eYear}`;
};

const formatEventLocation = ({ city, country, state }) => {
  return [city, state, country].filter((location) => !!location).join(', ');
};

const formatShowSeries = ({ episodeCount, seasonCount, strings }) => {
  const seasonMsg = `${seasonCount} ${
    seasonCount > 1 ? strings.seasons : strings.season
  }`;
  const episodeMsg = `${episodeCount} ${
    episodeCount > 1 ? strings.episodes : strings.episode
  }`;
  return episodeCount ? `${seasonMsg} · ${episodeMsg}` : seasonMsg;
};

const formatVideoDuration = ({ strings, videoDurationSeconds }) => {
  const hours = ~~(videoDurationSeconds / 3600);
  let minutes = ~~(videoDurationSeconds / 60) - hours * 60;
  let msg = `${hours}:${minutes < 10 ? `0${minutes}` : minutes}`;
  if (!hours) msg = `${minutes}`;
  if (!hours && !minutes) msg = '1';
  return `${msg} ${strings.min}`;
};

const CoverImage = ({
  hover = false,
  imageSrc,
  stacked = false,
  style = {},
  title,
}) => {
  const imageRef = useRef();
  const [imageLoaded, setImageLoaded] = useState(false);
  const showImage = imageRef && (imageLoaded || imageRef.complete);

  const onLoad = () => {
    setImageLoaded(true);
  };

  const onError = (e) => {
    e.target.src =
      'https://resources.redbull.tv/linear-borb/rbtv_background_landscape/im:i:h_900,w_600,c_fill';
  };

  return (
    <div
      className={`media-card__cover ${
        hover ? 'media-card__cover--hover' : ''
      } ${stacked ? 'media-card__cover--stacked' : ''}`}
      style={style}
    >
      <div className={`media-card__cover-image ${showImage ? 'show' : 'hide'}`}>
        <img
          alt={title}
          onError={onError}
          onLoad={onLoad}
          ref={imageRef}
          src={imageSrc}
        />
      </div>
    </div>
  );
};

const FavoriteButton = ({ isSaved, onClick, strings }) => (
  <button
    onClick={onClick}
    className={`media-card__favorite-button ${
      isSaved ? 'media-card__favorite-button--saved' : ''
    }`}
  >
    <IconSvg title={isSaved ? '' : strings.addToLibrary} type="star" />
  </button>
);

const EventCard = ({
  endDate,
  imageSrc,
  isSaved,
  location,
  onClick,
  startDate,
  strings,
  tag,
  title,
}) => (
  <div
    className={`media-card__body ${
      endDate && startDate ? 'media-card__body--ticket' : ''
    }`}
  >
    <FavoriteButton isSaved={isSaved} onClick={onClick} strings={strings} />
    <CoverImage hover imageSrc={imageSrc} title={title} />
    <div className="media-card__content">
      <p className="media-card__title">{title}</p>
      {endDate && startDate && (
        <p className="media-card__subtitle">
          <IconSvg type="calendar" />
          <span>{formatEventDate({ endDate, startDate })}</span>
        </p>
      )}
      {!!Object.keys(location).filter((key) => location[key]).length && (
        <p className="media-card__subtitle">
          {location.flagUrl && (
            <img src={location.flagUrl} title={location.country} />
          )}
          <span>{formatEventLocation(location)}</span>
        </p>
      )}
      {tag && (
        <div className="media-card__meta">
          <p className="media-card__meta-tag">{tag}</p>
        </div>
      )}
    </div>
  </div>
);

const FilmCard = ({ imageSrc, onClick, isSaved, strings, title }) => (
  <div className="media-card__body">
    <CoverImage hover imageSrc={imageSrc} title={title} />
    <FavoriteButton isSaved={isSaved} onClick={onClick} strings={strings} />
  </div>
);

const ShowCard = ({
  episodeCount,
  imageSrc,
  isSaved,
  onClick,
  seasonCount,
  strings,
  subHeading,
  tag,
  title,
}) => {
  // Show cover images must be responsive in comparison to metadata
  // Calculate dimensions based on respective containers and adjust
  // width/height to 2:3 aspect ratio
  const [coverArtDimensions, setCoverArtDimensions] = useState({
    height: 240,
    width: 160,
  });
  const bodyRef = useRef(null);
  const contentRef = useRef(null);

  useLayoutEffect(() => {
    if (bodyRef?.current && contentRef?.current) {
      const { height, paddingBottom } = getComputedStyle(bodyRef?.current);
      const coverHeight =
        parseFloat(height) -
        parseFloat(paddingBottom) -
        contentRef?.current?.clientHeight;
      const coverWidth = coverHeight * (2 / 3);
      setCoverArtDimensions({
        height: coverHeight,
        width: coverWidth,
      });
    }
  }, [bodyRef, contentRef]);

  return (
    <>
      <div className="media-card__background">
        <FavoriteButton isSaved={isSaved} onClick={onClick} strings={strings} />
      </div>
      <div className="media-card__body" ref={bodyRef}>
        <CoverImage
          imageSrc={imageSrc}
          stacked
          style={coverArtDimensions}
          title={title}
        />
        <div className="media-card__content" ref={contentRef}>
          <p className="media-card__title">{title}</p>
          <p className="media-card__subtitle">{subHeading}</p>
          <div className="media-card__meta">
            {!!seasonCount && !!episodeCount && (
              <p className="media-card__meta-series">
                {formatShowSeries({
                  episodeCount,
                  seasonCount,
                  strings,
                })}
              </p>
            )}
            {tag && <p className="media-card__meta-tag">{tag}</p>}
          </div>
        </div>
      </div>
    </>
  );
};

const StoryCard = ({
  imageSrc,
  isSaved,
  onClick,
  strings,
  subHeading,
  tag,
  teaser,
  title,
}) => (
  <div className="media-card__body">
    <FavoriteButton isSaved={isSaved} onClick={onClick} strings={strings} />
    <CoverImage hover imageSrc={imageSrc} title={title} />
    <div className="media-card__content">
      <p className="media-card__title">{title}</p>
      <p className="media-card__subtitle">{subHeading || teaser}</p>
      {tag && (
        <div className="media-card__meta">
          <p className="media-card__meta-tag">{tag}</p>
        </div>
      )}
    </div>
  </div>
);

const VideoCard = ({
  imageSrc,
  isGeoBlocked,
  isSaved,
  onClick,
  strings,
  subHeading,
  tag,
  title,
  videoDurationSeconds,
}) => (
  <>
    {isGeoBlocked && (
      <div className="media-card__background--geoblock">
        <p>{strings.thisVideo}</p>
      </div>
    )}
    <CoverImage hover imageSrc={imageSrc} title={title} />
    <FavoriteButton isSaved={isSaved} onClick={onClick} strings={strings} />
    <div className="media-card__play-button">
      <IconSvg type="play" />
    </div>
    <div className="media-card__content">
      <p className="media-card__title">{title}</p>
      <p className="media-card__subtitle">{subHeading}</p>
      {(tag || !!videoDurationSeconds) && (
        <div className="media-card__meta">
          <p className="media-card__meta-tag">{tag}</p>
          <p className="media-card__meta-duration">
            {formatVideoDuration({
              strings,
              videoDurationSeconds,
            })}
          </p>
        </div>
      )}
    </div>
  </>
);

const CARD_MAP = {
  EVENT: EventCard,
  FILM: FilmCard,
  SHOW: ShowCard,
  STORY: StoryCard,
  VIDEO: VideoCard,
};

const MediaCard = (props) => {
  const isGeoBlocked = props.geoBlockedIn.includes(props.country);
  const { onActionClick, onClick, ...rest } = props;
  const normalizedType = Object.keys(CARD_MAP).find((key) => {
    return props.type.includes(key);
  });
  const Card = CARD_MAP[normalizedType];
  const handleActionClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    props.onActionClick(props.id);
  };
  const handleClick = () => props.onClick(props.id);

  return (
    <a
      className={`media-card media-card__${normalizedType.toLowerCase()}-card`}
      onClick={handleClick}
      href={props.url}
    >
      <div className="media-card__wrapper">
        <Card
          isGeoBlocked={isGeoBlocked}
          onClick={handleActionClick}
          {...rest}
        />
      </div>
    </a>
  );
};

MediaCard.propTypes = {
  country: PropTypes.string.isRequired,
  episodeCount: PropTypes.number,
  geoBlockedIn: PropTypes.array,
  id: PropTypes.string.isRequired,
  imageSrc: PropTypes.string.isRequired,
  isSaved: PropTypes.bool.isRequired,
  location: PropTypes.object,
  onActionClick: PropTypes.func.isRequired,
  onClick: PropTypes.func,
  tag: PropTypes.string.isRequired,
  teaser: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  type: PropTypes.string.isRequired,
  seasonCount: PropTypes.number,
  subHeading: PropTypes.string,
  strings: PropTypes.object.isRequired,
  style: PropTypes.object,
  url: PropTypes.string.isRequired,
  videoDurationSeconds: PropTypes.number,
};

MediaCard.defaultProps = {
  isSaved: false,
  onClick: () => {},
  style: {},
};

export default MediaCard;
